import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const RegisterPage = () => {
  const { register, user } = useAuth();
  const { t, lang, setLang, LANGS } = useI18n();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    company_name: '',
    contact_name: '',
    phone: '',
    address: '',
    city: '',
    postal_code: '',
  });
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [user, navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(formData);
      toast.success(t('registerSuccess'));
      navigate('/');
    } catch (error) {
      toast.error(t('registerFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleRegister = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Language Selector - Top Right */}
      <div className="fixed top-4 right-4 z-50">
        <select
          value={lang}
          onChange={(e) => setLang(e.target.value)}
          className="bg-white/90 backdrop-blur border border-gray-300 rounded-md px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
          data-testid="language-selector"
        >
          {LANGS.map((l) => (
            <option key={l.code} value={l.code}>
              {l.flag} {l.label}
            </option>
          ))}
        </select>
      </div>

      {/* Mobile Hero Image */}
      <div
        className="lg:hidden h-56 relative"
        style={{
          backgroundImage: 'url(https://customer-assets-agu9un31.emergentagent.net/job_79549384-9d12-485d-8143-ee256e051cae/artifacts/1s5py9ht_ChatGPT%20Image%2026.%20Juli%202026%2C%2017_54_31.png)',
          backgroundSize: 'contain',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundColor: '#1a1a1a',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-white" />
      </div>

      {/* Desktop Hero Image */}
      <div
        className="hidden lg:flex lg:w-1/2 relative items-center justify-center"
        style={{
          backgroundColor: '#1a1a1a',
        }}
      >
        <div
          className="w-full h-full"
          style={{
            backgroundImage: 'url(https://customer-assets-agu9un31.emergentagent.net/job_79549384-9d12-485d-8143-ee256e051cae/artifacts/1s5py9ht_ChatGPT%20Image%2026.%20Juli%202026%2C%2017_54_31.png)',
            backgroundSize: 'contain',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        />
      </div>

      {/* Right Side - Register Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Logo direkt über Registrieren */}
          <div className="mb-6 text-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_barber-supply/artifacts/e1ft8m5t_ED_STEEL_Logo_White_BG_Print.png" 
              alt="ED STEEL" 
              className="h-20 w-auto mx-auto mb-4"
            />
            <h2 className="text-3xl font-heading font-bold mb-2">{t('register')}</h2>
            <p className="text-muted-foreground">{t('createAccount')}</p>
          </div>

          {/* Google Register Button */}
          <Button
            type="button"
            variant="outline"
            className="w-full h-12 mb-4 flex items-center justify-center gap-3 border-2 hover:bg-gray-50"
            onClick={handleGoogleRegister}
            data-testid="google-register-button"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            {t('registerWithGoogle')}
          </Button>

          <div className="relative mb-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-gray-500">{t('or')}</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3" data-testid="register-form">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label htmlFor="company_name">{t('companyName')}</Label>
                <Input
                  id="company_name"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                  required
                  className="h-11 mt-1"
                  data-testid="company-name-input"
                />
              </div>

              <div className="col-span-2">
                <Label htmlFor="contact_name">{t('contactName')}</Label>
                <Input
                  id="contact_name"
                  name="contact_name"
                  value={formData.contact_name}
                  onChange={handleChange}
                  required
                  className="h-11 mt-1"
                  data-testid="contact-name-input"
                />
              </div>

              <div className="col-span-2">
                <Label htmlFor="email">{t('email')}</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="h-11 mt-1"
                  data-testid="email-input"
                />
              </div>

              <div className="col-span-2">
                <Label htmlFor="phone">
                  {t('phone')} <span className="text-xs text-gray-500">{t('forSms')}</span>
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  placeholder="+49 171 1234567"
                  className="h-11 mt-1"
                  data-testid="phone-input"
                />
              </div>

              <div className="col-span-2">
                <Label htmlFor="address">{t('address')}</Label>
                <Input
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  required
                  className="h-11 mt-1"
                  data-testid="address-input"
                />
              </div>

              <div>
                <Label htmlFor="city">{t('city')}</Label>
                <Input
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  required
                  className="h-11 mt-1"
                  data-testid="city-input"
                />
              </div>

              <div>
                <Label htmlFor="postal_code">{t('postalCode')}</Label>
                <Input
                  id="postal_code"
                  name="postal_code"
                  value={formData.postal_code}
                  onChange={handleChange}
                  required
                  className="h-11 mt-1"
                  data-testid="postal-code-input"
                />
              </div>

              <div className="col-span-2">
                <Label htmlFor="password">{t('password')}</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  minLength={8}
                  placeholder="Mindestens 8 Zeichen"
                  className="h-11 mt-1"
                  data-testid="password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-11 text-lg font-heading"
              disabled={loading}
              data-testid="register-button"
            >
              {loading ? t('loading') : t('register')}
            </Button>
          </form>

          <div className="mt-4 text-center">
            <p className="text-muted-foreground">
              {t('haveAccount')}{' '}
              <Link to="/login" className="text-primary font-medium hover:underline" data-testid="login-link">
                {t('login')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;